import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AchievementNotificationService } from '../../services/achievement-notification/achievement-notification.service';
import {
  Achievement,
  EventType,
} from '../../../pages/achievements/models/achievements';
import {
  BadgeTierInfo,
  EVENT_TYPE_LABELS,
  SECTION_THEMES,
  ConfettiParticle,
  getAchievementTierInfo,
  getAchievementIconSlug,
  getThemeConfettiColors,
} from '../../../pages/achievements/models/achievement-badges';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'app-achievement-unlock-modal',
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
  templateUrl: './achievement-unlock-modal.component.html',
  styleUrl: './achievement-unlock-modal.component.scss',
})
export class AchievementUnlockModalComponent implements OnInit, OnDestroy {
  @ViewChild('unlockConfettiCanvas')
  confettiCanvasRef?: ElementRef<HTMLCanvasElement>;

  private achievementService = inject(AchievementNotificationService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  activeAchievement: Achievement | null = null;
  currentIndex = 0;
  totalCount = 0;

  private activeParticles: ConfettiParticle[] = [];
  private animationFrameId: number | null = null;
  private sub = new Subscription();

  readonly EVENT_TYPE_LABELS = EVENT_TYPE_LABELS;
  readonly SECTION_THEMES = SECTION_THEMES;

  get tier(): BadgeTierInfo {
    return getAchievementTierInfo(this.activeAchievement);
  }

  get iconKey(): string {
    return getAchievementIconSlug(this.activeAchievement?.title);
  }

  get categoryLabel(): string {
    if (!this.activeAchievement?.event_type) return 'Achievement';
    return (
      EVENT_TYPE_LABELS[this.activeAchievement.event_type] ??
      this.activeAchievement.event_type_name ??
      'Achievement'
    );
  }

  get categoryThemeKey(): string {
    if (!this.activeAchievement?.event_type) return 'theme-blue';
    return (
      SECTION_THEMES[this.activeAchievement.event_type]?.key ?? 'theme-blue'
    );
  }

  get hasMoreInQueue(): boolean {
    return this.currentIndex < this.totalCount;
  }

  ngOnInit(): void {
    this.sub.add(
      this.achievementService.activeAchievement$.subscribe((achievement) => {
        this.activeAchievement = achievement;
        if (achievement) {
          // Trigger confetti celebration on every newly displayed achievement
          setTimeout(() => this.launchConfetti(), 150);
        } else {
          this.cleanupAnimation();
        }
      }),
    );

    this.sub.add(
      this.achievementService.currentIndex$.subscribe((idx) => {
        this.currentIndex = idx;
      }),
    );

    this.sub.add(
      this.achievementService.totalCount$.subscribe((count) => {
        this.totalCount = count;
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.cleanupAnimation();
  }

  onNextOrClose(): void {
    this.achievementService.next();
  }

  onDismiss(): void {
    this.achievementService.dismiss();
  }

  onViewAllAchievements(): void {
    this.achievementService.dismiss();
    this.router.navigate(['/achievements']);
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onNextOrClose();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.activeAchievement) {
      this.onDismiss();
    }
  }

  // ─── Particle Confetti Engine ──────────────────────────────────────────────

  launchConfetti(): void {
    const canvas = this.confettiCanvasRef?.nativeElement;
    if (!canvas) return;

    if (
      canvas.width !== window.innerWidth ||
      canvas.height !== window.innerHeight
    ) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const originX = window.innerWidth / 2;
    const originY = window.innerHeight * 0.38;

    const colors = [
      ...this.tier.confettiColors,
      ...getThemeConfettiColors(
        this.activeAchievement?.event_type ?? EventType.REFER_LEAD,
      ),
    ];

    const burst = this.spawnBurstParticles(originX, originY, colors, 95);
    const rain = this.spawnRainParticles(colors, 40);

    this.activeParticles.push(...burst, ...rain);
    this.startParticleAnimation();
  }

  private spawnBurstParticles(
    originX: number,
    originY: number,
    colors: string[],
    count = 95,
  ): ConfettiParticle[] {
    const shapes: ('rect' | 'circle' | 'star' | 'ribbon')[] = [
      'rect',
      'circle',
      'star',
      'ribbon',
    ];

    return Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 4 + Math.random() * 9.5;
      const shape = shapes[Math.floor(Math.random() * shapes.length)];

      return {
        x: originX + (Math.random() - 0.5) * 36,
        y: originY + (Math.random() - 0.5) * 36,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3.2,
        size: shape === 'star' ? 8 + Math.random() * 6 : 5 + Math.random() * 6,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 16,
        tiltAngle: Math.random() * Math.PI,
        tiltSpeed: (Math.random() - 0.5) * 0.25,
        shape,
        opacity: 1,
        life: 0,
        maxLife: 95 + Math.random() * 45,
        drag: 0.955,
        gravity: 0.16,
      };
    });
  }

  private spawnRainParticles(colors: string[], count = 40): ConfettiParticle[] {
    const width = window.innerWidth;
    const shapes: ('rect' | 'circle' | 'ribbon')[] = [
      'rect',
      'circle',
      'ribbon',
    ];

    return Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * 80,
      vx: (Math.random() - 0.5) * 3,
      vy: 2.2 + Math.random() * 4,
      size: 6 + Math.random() * 6,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      tiltAngle: Math.random() * Math.PI,
      tiltSpeed: (Math.random() - 0.5) * 0.15,
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      opacity: 1,
      life: 0,
      maxLife: 150 + Math.random() * 40,
      drag: 0.99,
      gravity: 0.08,
    }));
  }

  private startParticleAnimation(): void {
    if (this.animationFrameId !== null) return;

    const canvas = this.confettiCanvasRef?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (
        canvas.width !== window.innerWidth ||
        canvas.height !== window.innerHeight
      ) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = this.activeParticles.length - 1; i >= 0; i--) {
        const p = this.activeParticles[i];

        p.vx *= p.drag;
        p.vy = p.vy * p.drag + p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.tiltAngle += p.tiltSpeed;
        p.life++;

        if (p.life > p.maxLife * 0.65) {
          const remaining = p.maxLife - p.life;
          const fadeDuration = p.maxLife * 0.35;
          p.opacity = Math.max(0, remaining / fadeDuration);
        }

        if (
          p.opacity <= 0 ||
          p.y > canvas.height + 50 ||
          p.x < -50 ||
          p.x > canvas.width + 50
        ) {
          this.activeParticles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        const tilt = Math.sin(p.tiltAngle);

        if (p.shape === 'rect') {
          ctx.fillRect(
            -p.size / 2,
            (-p.size / 2) * tilt,
            p.size,
            p.size * tilt * 0.6 + 2,
          );
        } else if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'star') {
          ctx.beginPath();
          const s = p.size * 0.85;
          ctx.moveTo(0, -s);
          ctx.quadraticCurveTo(0, 0, s, 0);
          ctx.quadraticCurveTo(0, 0, 0, s);
          ctx.quadraticCurveTo(0, 0, -s, 0);
          ctx.quadraticCurveTo(0, 0, 0, -s);
          ctx.fill();
        } else {
          ctx.fillRect(
            -p.size * 0.8,
            -p.size * 0.25 * tilt,
            p.size * 1.6,
            p.size * 0.5 * tilt + 1,
          );
        }
        ctx.restore();
      }

      if (this.activeParticles.length > 0) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.animationFrameId = null;
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(animate);
    });
  }

  private cleanupAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.activeParticles = [];
    const canvas = this.confettiCanvasRef?.nativeElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
}
